// Yogi brand color tokens — OKLCH strings for styled-components + chart consumers.
// Generated from anchor hexes as OKLCH ramps (anchor → 500).
// Mirror: sutra.css :root custom properties hold these same values for CSS authors.
//
// Scale key guide (matches Tailwind v4 convention):
//   50–400  lighter tints; 50 is near-white
//   500     the brand/accent color (former "base")
//   600     one step darker (former "deep" equivalent)
//   700–950 progressively darker shades; 950 is near-black

// ── Neutral gray scale ────────────────────────────────────────────────────────
// Converted verbatim from existing hex values; consumers use gray[100], gray[200] etc.

export const gray = {
  50: 'oklch(98.2% 0 0)',
  100: 'oklch(97.0% 0 0)',
  200: 'oklch(93.7% 0.0017 247.84)',
  300: 'oklch(87.8% 0.0047 258.33)',
  400: 'oklch(72.8% 0.0106 267.33)',
  500: 'oklch(56.5% 0.0165 264.43)',
  600: 'oklch(46.0% 0.0174 264.39)',
  700: 'oklch(39.4% 0.0197 266.05)',
  800: 'oklch(28.9% 0.0236 264.1)',
  900: 'oklch(23.0% 0.0209 264.04)', // #181D27
  950: 'oklch(15.8% 0.0118 260.62)', // #0A0D12
} as const

// ── Brand ────────────────────────────────────────────────────────────────────

export const brand = {
  white: 'oklch(100% 0 0)',

  // Note: brand navy #28364F anchors at 800 (not 500) because navy is intrinsically
  // dark; a perceptually-even 50→950 ramp would place it well past the midpoint.
  // All other ramps keep their base at 500.
  stillNight: {
    50: 'oklch(96.5% 0.0064 261.6)',
    100: 'oklch(93.0% 0.0120 261.6)',
    200: 'oklch(86.0% 0.0220 261.6)',
    300: 'oklch(77.0% 0.0330 261.6)',
    400: 'oklch(66.0% 0.0430 261.6)',
    500: 'oklch(53.0% 0.0480 261.6)',
    600: 'oklch(46.0% 0.0500 261.6)',
    700: 'oklch(40.0% 0.0495 261.6)',
    800: 'oklch(33.2% 0.0483 261.6)', // ← primary navy (#28364F)
    900: 'oklch(24.0% 0.0380 261.6)',
    950: 'oklch(14.0% 0.0240 261.6)',
  },

  sunriseCoral: {
    50: 'oklch(97.5% 0.0123 32.86)',
    100: 'oklch(92.3% 0.0287 32.86)',
    200: 'oklch(87.1% 0.0573 32.86)',
    300: 'oklch(82.0% 0.0985 32.86)',
    400: 'oklch(76.8% 0.1387 32.86)',
    500: 'oklch(71.6% 0.1791 32.86)', // ← former base  (#FF7155)
    600: 'oklch(62.0% 0.1666 32.86)', // ← former deep  (~#DC3B2F)
    700: 'oklch(52.5% 0.1469 32.86)',
    800: 'oklch(42.9% 0.12 32.86)',
    900: 'oklch(33.4% 0.0932 32.86)',
    950: 'oklch(23.8% 0.0663 32.86)',
  },
} as const

// ── Accent families (50–950 ramps) ───────────────────────────────────────────

export const accents = {
  emeraldAura: {
    50: 'oklch(97.5% 0.0071 169.31)',
    100: 'oklch(89.3% 0.0163 169.31)',
    200: 'oklch(81.1% 0.0326 169.31)',
    300: 'oklch(72.9% 0.056 169.31)',
    400: 'oklch(64.7% 0.0814 169.31)',
    500: 'oklch(56.5% 0.1018 169.31)', // ← former base  (#23896C)
    600: 'oklch(50.0% 0.0947 169.31)', // ← former deep  (~#0A684D)
    700: 'oklch(43.4% 0.0835 169.31)',
    800: 'oklch(36.9% 0.0682 169.31)',
    900: 'oklch(30.3% 0.0529 169.31)',
    950: 'oklch(23.8% 0.0377 169.31)',
  },

  ceruleanFlow: {
    50: 'oklch(97.5% 0.0073 223.04)',
    100: 'oklch(89.3% 0.0168 223.04)',
    200: 'oklch(81.0% 0.0336 223.04)',
    300: 'oklch(72.8% 0.0577 223.04)',
    400: 'oklch(64.5% 0.0839 223.04)',
    500: 'oklch(56.3% 0.1049 223.04)', // ← former base  (#0082A2)
    600: 'oklch(49.8% 0.0927 223.04)', // ← former deep  (~#006078)
    700: 'oklch(43.3% 0.0806 223.04)',
    800: 'oklch(36.8% 0.0686 223.04)',
    900: 'oklch(30.3% 0.0545 223.04)',
    950: 'oklch(23.8% 0.0388 223.04)',
  },

  reflectionBlue: {
    50: 'oklch(97.5% 0.0062 257.32)',
    100: 'oklch(88.9% 0.0141 257.32)',
    200: 'oklch(80.4% 0.0283 257.32)',
    300: 'oklch(71.8% 0.0486 257.32)',
    400: 'oklch(63.3% 0.0707 257.32)',
    500: 'oklch(54.7% 0.0883 257.32)', // ← former base  (#4F72A4)
    600: 'oklch(48.6% 0.0822 257.32)', // ← former deep  (~#375D93)
    700: 'oklch(42.4% 0.0724 257.32)',
    800: 'oklch(36.2% 0.0592 257.32)',
    900: 'oklch(30.0% 0.0459 257.32)',
    950: 'oklch(23.8% 0.0327 257.32)',
  },

  goldenLight: {
    50: 'oklch(97.5% 0.0088 75.97)',
    100: 'oklch(95.1% 0.0202 75.97)',
    200: 'oklch(92.7% 0.0403 75.97)',
    300: 'oklch(90.4% 0.0693 75.97)',
    400: 'oklch(88.0% 0.1008 75.97)',
    500: 'oklch(85.6% 0.126 75.97)', // ← former base  (#FFC46B)
    600: 'oklch(73.2% 0.1172 75.97)', // ← former deep  (~#C37500)
    700: 'oklch(60.9% 0.1033 75.97)',
    800: 'oklch(48.5% 0.0844 75.97)',
    900: 'oklch(36.2% 0.0655 75.97)',
    950: 'oklch(23.8% 0.0466 75.97)',
  },

  violetHorizon: {
    50: 'oklch(97.5% 0.0089 293.75)',
    100: 'oklch(90.4% 0.0203 293.75)',
    200: 'oklch(83.4% 0.0405 293.75)',
    300: 'oklch(76.3% 0.0696 293.75)',
    400: 'oklch(69.2% 0.1013 293.75)',
    500: 'oklch(62.1% 0.1266 293.75)', // ← former base  (#8B76CB)
    600: 'oklch(54.5% 0.1177 293.75)', // ← former deep  (~#6447BB)
    700: 'oklch(46.8% 0.1038 293.75)',
    800: 'oklch(39.1% 0.0848 293.75)',
    900: 'oklch(31.5% 0.0658 293.75)',
    950: 'oklch(23.8% 0.0468 293.75)',
  },

  crimsonBloom: {
    50: 'oklch(97.5% 0.0124 10.22)',
    100: 'oklch(89.6% 0.0322 10.22)',
    200: 'oklch(81.7% 0.0644 10.22)',
    300: 'oklch(73.8% 0.1107 10.22)',
    400: 'oklch(65.9% 0.161 10.22)',
    500: 'oklch(58.0% 0.2012 10.22)', // ← former base  (#D52F5E)
    600: 'oklch(51.1% 0.1871 10.22)', // ← former deep  (~#B60135)
    700: 'oklch(44.3% 0.165 10.22)',
    800: 'oklch(37.5% 0.1348 10.22)',
    900: 'oklch(30.6% 0.1046 10.22)',
    950: 'oklch(23.8% 0.0745 10.22)',
  },

  verdantGreen: {
    50: 'oklch(97.5% 0.0109 126.99)',
    100: 'oklch(93.1% 0.0248 126.99)',
    200: 'oklch(88.7% 0.0496 126.99)',
    300: 'oklch(84.3% 0.0853 126.99)',
    400: 'oklch(79.9% 0.124 126.99)',
    500: 'oklch(75.5% 0.155 126.99)', // ← former base  (#95C14A)
    600: 'oklch(65.2% 0.1442 126.99)', // ← former deep  (~#669614)
    700: 'oklch(54.8% 0.1271 126.99)',
    800: 'oklch(44.5% 0.1039 126.99)',
    900: 'oklch(34.1% 0.0806 126.99)',
    950: 'oklch(23.8% 0.0574 126.99)',
  },
} as const

// ── Semantic roles ────────────────────────────────────────────────────────────
// Use these in components; reference brand/accent/gray primitives only in token files.

export const semantic = {
  fg: brand.stillNight[800], // headlines, icons
  fgMuted: gray[600], // body text
  fgSubtle: gray[400], // metadata, captions
  fgOnDark: brand.white,

  bg: brand.white,
  bgSubtle: gray[50],
  bgDark: brand.stillNight[800],

  border: gray[200],
  borderStrong: gray[300],
  borderOnDark: 'oklch(100% 0 0 / 0.12)',

  accent: brand.stillNight[800], // = stillNight[800] — primary navy (#28364F)
  accentTint: brand.stillNight[50], // = stillNight[50]  — lightest navy tint
  accentDeep: brand.stillNight[900], // = stillNight[900] — one step darker
} as const

// ── Convenience re-export ────────────────────────────────────────────────────

export const colors = { brand, accents, gray, semantic } as const
export type Colors = typeof colors
